<script setup lang="ts">
/**
 * A large headline input (a record's title) with an underline instead of a
 * frame. Under an `NxLoadingWrapper` it shows the save status as a corner badge.
 *
 * The root is a `<span>` that anchors the badge; every attribute — `class`,
 * `style`, listeners, `aria-*` — still lands on the `<input>`. A parent's
 * scoped style does not reach the input, though: Vue puts the parent's scope
 * id on the root span only, so a scoped rule for a passed `class` matches
 * nothing. Inline `style` still applies.
 */
import { computed, useAttrs, useTemplateRef, type InputHTMLAttributes } from 'vue';
import { useBrowserAutofillOffAttrs } from './browserAutofill';
import NxSaveStateBadge from './NxSaveStateBadge.vue';
import { useInputSaveStatus } from './composables/useInputSaveStatus';

const props = defineProps<
  {
    placeholder?: string;
    /**
     * Locks the input. A declared Boolean prop, not a passed-through attribute,
     * so a bare `disabled` (an empty string as an attribute) counts as `true`.
     */
    disabled?: boolean;
  } & /* @vue-ignore */ Omit<InputHTMLAttributes, 'value' | 'disabled'>
>();

defineOptions({ inheritAttrs: false });

const model = defineModel<string>({ required: true });

// Save status (loading/success/error) from an ancestor NxLoadingWrapper. While
// it saves, the input locks like every other input.
const {
  visualState,
  errorMessage,
  fadeOut,
  srText,
  ariaBindings,
  disabled: statusDisabled,
} = useInputSaveStatus();
const inputDisabled = computed(() => props.disabled || statusDisabled.value);
const inputRef = useTemplateRef('input-ref');
const attrs = useAttrs();
const browserAutofillOffAttrs = useBrowserAutofillOffAttrs();
const autofillAttrs = computed(() => browserAutofillOffAttrs('text', attrs.autocomplete));
const autofillWins = computed(() =>
  autofillAttrs.value.autocomplete ? { autocomplete: autofillAttrs.value.autocomplete } : {}
);

defineExpose({
  focus: (options?: FocusOptions) => inputRef.value?.focus(options),
});
</script>

<template>
  <span class="nx-display-input-root">
    <input
      ref="input-ref"
      v-model="model"
      class="nx-display-input"
      type="text"
      :placeholder="placeholder"
      :data-save-status="visualState"
      v-bind="{ ...autofillAttrs, ...$attrs, ...autofillWins, ...ariaBindings }"
      :disabled="inputDisabled"
    />
    <NxSaveStateBadge
      class="save-badge"
      :state="visualState"
      :error-message="errorMessage"
      :fade-out="fadeOut"
    />
    <span class="nx-sr-only" aria-live="polite">{{ srText }}</span>
  </span>
</template>

<style scoped>
/* `type="search"` only keeps browser autofill out (browserAutofill.ts): no
   search styling, no clear button. */
input[type='search'] {
  appearance: none;
}

input[type='search']::-webkit-search-cancel-button,
input[type='search']::-webkit-search-decoration {
  appearance: none;
  display: none;
}

/* Hugs the input and anchors the corner badge. */
.nx-display-input-root {
  position: relative;
  display: block;
}
.save-badge {
  position: absolute;
  top: calc(-1 * var(--nxt-space-md) * 0.438);
  right: calc(-1 * var(--nxt-space-md) * 0.438);
}
.nx-sr-only {
  position: absolute;
  width: var(--nxt-hairline);
  height: var(--nxt-hairline);
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/* TODO: adapt to css-vars */
.nx-display-input {
  width: 100%;
  border: 0;
  border-bottom: calc(var(--nxt-hairline) * 2) solid var(--nxt-border);
  background: transparent;
  padding: var(--nxt-space-2xs) 0 var(--nxt-space-xs);
  font-family: var(--nxt-font);
  font-size: var(--nxt-font-size-2xl);
  font-weight: var(--nxt-font-weight-semibold);
  color: var(--nxt-fg);
  outline: none;
  transition:
    border-color 0.15s,
    box-shadow 0.15s;
}

.nx-display-input::placeholder {
  color: var(--nxt-fg-subtle);
  font-weight: var(--nxt-font-weight-medium);
}

.nx-display-input:focus {
  border-bottom-color: var(--nxt-input-border-focus);
  box-shadow: 0 var(--nxt-hairline) 0 0 var(--nxt-input-border-focus);
}

/* Disabled — by the caller or while an NxLoadingWrapper saves: muted text like
   a framed field's (style.css), the underline at rest. Without it the full ink
   of `.nx-display-input` hides the browser's own disabled grey. */
.nx-display-input:disabled {
  color: var(--nxt-input-fg-disabled);
  cursor: not-allowed;
}
.nx-display-input:disabled::placeholder {
  color: var(--nxt-input-fg-disabled);
}
</style>
